import { createRouteParams } from "@prismicio/editor-support/URL"
import {
  type Document as DocumentT,
  fillDocumentWithDefaultValues,
  GroupContent,
  traverseDocument
} from "@prismicio/types-internal/lib/content"
import {
  flattenSections,
  type StaticCustomType
} from "@prismicio/types-internal/lib/customtypes"

export function prepareDocumentForSave(
  customType: StaticCustomType,
  document: DocumentT
): DocumentT {
  const documentWithAutoGeneratedGroups = autoGenerateGroupItems(
    customType,
    document
  )

  const documentWithValidSlices = filterInvalidSlices(
    customType,
    documentWithAutoGeneratedGroups
  )

  return fillDocumentWithDefaultValues(customType, documentWithValidSlices)
}

function autoGenerateGroupItems(
  customType: StaticCustomType,
  document: DocumentT
): DocumentT {
  const fields = flattenSections(customType)

  return fields.reduce<DocumentT>((filledDocument, [fieldKey, fieldDef]) => {
    const fieldContent = document[fieldKey]

    const noContentForGroup = !fieldContent && fieldDef.type === "Group"

    if (noContentForGroup)
      return {
        ...filledDocument,
        [fieldKey]: defaultGroupContentWithOneItem
      }

    return filledDocument
  }, document)
}

function filterInvalidSlices(
  customType: StaticCustomType,
  document: DocumentT
) {
  const documentWithValidSlices = traverseDocument({
    document,
    customType
  })({
    transformSlice: (slice) => {
      // Remove slice if it doesn't exist in the custom type
      if (!slice.model) return

      return slice.content
    }
  })

  return documentWithValidSlices
}

export const defaultGroupContentWithOneItem: GroupContent = {
  __TYPE__: "GroupContentType",
  value: [{ __TYPE__: "GroupItemContent", value: [] }]
}

// Single source of truth for any visual Prismic document status mapping.
export const documentStatus = {
  unclassified: {
    title: "Draft",
    color: "amber"
  },
  published: {
    title: "Live",
    color: "green"
  },
  release: {
    title: "Planned",
    color: "purple"
  },
  archived: {
    title: "Archived",
    color: "grey"
  }
} as const

export const searchDocumentStatus = {
  unclassified: ["unclassified", "published"],
  archived: ["unclassified", "published"],
  release: ["release", "published"],
  published: ["published"]
}

interface getDocumentUrlArgs {
  documentId: string
  status?: string
  releaseId?: string
  versionId?: string
  baseUrl: URL
}

export function getDocumentUrl(args: getDocumentUrlArgs): URL {
  const { documentId, versionId, baseUrl, status, releaseId } = args

  const params = { s: status, v: versionId, r: releaseId }
  const newSearchParams = createRouteParams({ params })
  const urlParams = new URLSearchParams(newSearchParams).toString()

  return new URL(`pages/${documentId}?${urlParams}`, baseUrl)
}
